import * as React from "react";
import {
  Text,
  Switch,
  Button,
  Divider,
  Caption1,
  tokens,
  Link,
} from "@fluentui/react-components";
import {
  WeatherSunny24Regular,
  WeatherMoon24Regular,
  Save24Regular,
  Apps24Regular,
  Mail24Regular,
  ShieldCheckmark24Regular,
  Star24Regular,
} from "@fluentui/react-icons";
import { useTranslation } from 'react-i18next';
import { useSettings, ServiceType } from "../../../contexts/SettingsContext";

import SettingsSection from "./SettingsSection";
import SettingRow from "./SettingRow";
import LanguageSelector from "./LanguageSelector";
import ServiceSelector from "./ServiceSelector";
import ApiConfiguration from "./ApiConfiguration";
import MessageDialog from "../../UI/MessageDialog";
import { useSettingsStyles } from "./style";

const SUPPORT_EMAIL = "support@articentgroup.com";

const PRIVACY_POLICY_URL = "https://articentgroup.com/privacy-policy/";
const MORE_APPS_URL = "https://apps.microsoft.com/search/publisher?name=Art%20Group&publisher=Art%20Group";
const RATE_US_URL = "https://appsource.microsoft.com/"; // TODO: Change it

interface SettingsPageProps {
  onToggleTheme?: () => void;
  isDarkMode?: boolean;
}

const SettingsPage: React.FC<SettingsPageProps> = (props: SettingsPageProps) => {
  const { onToggleTheme, isDarkMode = false } = props;
  const { t, i18n } = useTranslation();
  const { settings, updateSettings } = useSettings();

  const styles = useSettingsStyles();
  
  const [localApiKey, setLocalApiKey] = React.useState(settings.apiKey);
  const [localApiUrl, setLocalApiUrl] = React.useState(settings.apiUrl);
  const [selectedLanguage, setSelectedLanguage] = React.useState(i18n.language);
  const [selectedService, setSelectedService] = React.useState<ServiceType>(settings.selectedService);

  const [alertOpen, setAlertOpen] = React.useState(false);

  const [alertConfig, setAlertConfig] = React.useState({
    title: '',
    message: '',
  });

  React.useEffect(() => {
    document.title = t('header.menu.settings', 'Settings');
  }, []);

  const getFeedbackMailto = () => {
    const subject = encodeURIComponent(t('settings.feedback.subject'));
    const body = encodeURIComponent(t('settings.feedback.body'));
    return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  };

  const handleSendFeedback = () => {
    const mailtoString = getFeedbackMailto();
    
    window.open(mailtoString, '_blank');
  };

  const showAlert = (title: string, message: string) => {
    setAlertConfig({
      title: title,
      message: message
    });
    setAlertOpen(true);
  };

  const handleLanguageChange = (langCode: string) => {
    setSelectedLanguage(langCode);
    i18n.changeLanguage(langCode);
  };

  const handleServiceChange = (service: ServiceType) => {
    setSelectedService(service);
    const defaultUrlProviders: ServiceType[] = ['openai', 'anthropic'];
    updateSettings({ 
      selectedService: service,
      apiUrl: defaultUrlProviders.includes(service) ? '' : settings.apiUrl
    });
  };
  
  const handleApiKeyChange = (apiKey: string) => {
    setLocalApiKey(apiKey);
    updateSettings({ apiKey });
  };

  const handleApiUrlChange = (apiUrl: string) => {
    setLocalApiUrl(apiUrl);
    updateSettings({ apiUrl });
  };

  const handleAutoSaveChange = (checked: boolean) => {
    updateSettings({ autoSave: checked });
  };


  // Get all translated strings
  const appearanceTitle = t('settings.appearance', 'Appearance');
  const themeLabel = t('settings.theme', 'Theme');
  const themeDescription = t('settings.themeDescription', 'Choose between light and dark theme');
  const languageLabel = t('settings.language', 'Language');
  const languageDescription = t('settings.languageDescription', 'Select your preferred language');
  const generalTitle = t('settings.general', 'General');
  const settingsSaved = t('settings.saved', 'Settings saved successfully!');
  const autoSaveLabel = t('settings.autoSave', 'Auto-save conversations');
  const autoSaveDescription = t('settings.autoSaveDescription', 'Automatically save your chat history');
  
  const apiConfigTitle = t('settings.apiConfiguration', 'API Configuration');
  const apiKeyLabel = t('settings.apiKey', 'API Key');
  const apiKeyPlaceholder = t('settings.apiKeyPlaceholder', 'Enter your API key');
  const apiKeyDescription = t('settings.apiKeyDescription', 'Your API key is stored locally and never sent to our servers');
  const saveSettingsLabel = t('settings.saveSettings', 'Save Settings');
  
  const aboutTitle = t('settings.about', 'About');

  // Theme switch labels
  const lightModeLabel = t('header.menu.lightMode', 'Light');
  const darkModeLabel = t('header.menu.darkMode', 'Dark');
  const currentThemeLabel = isDarkMode ? lightModeLabel : darkModeLabel;
  const themeSwitchAriaLabel = isDarkMode ? 
    t('settings.switchToLight', 'Switch to light theme') : 
    t('settings.switchToDark', 'Switch to dark theme');

  // Service selector translations
  const serviceLabel = t('settings.service', 'AI Service');
  const serviceDescription = t('settings.serviceDescription', 'Select which AI service to use');
  const openAILabel = t('settings.openAI', 'OpenAI');
  const azureLabel = t('settings.azure', 'Azure OpenAI');
  const openAICompatibleLabel = t('settings.openAICompatible', 'OpenAI Compatible');
  const anthropicLabel = t('settings.anthropic', 'Anthropic (Claude)');
  const bedrockLabel = t('settings.bedrock', 'Amazon Bedrock');
  const bedrockAgentCoreLabel = t('settings.bedrockAgentCore', 'Amazon Bedrock AgentCore');
  const apiUrlLabel = t('settings.apiUrl', 'API URL');
  const apiUrlPlaceholder = t('settings.apiUrlPlaceholder', 'Enter API endpoint URL');
  const apiUrlDescription = t('settings.apiUrlDescription', 'Custom API endpoint for OpenAI compatible services');

  const handleSaveSettings = () => {
    updateSettings({
      apiKey: localApiKey,
      apiUrl: (selectedService === 'openai' || selectedService === 'anthropic') ? '' : localApiUrl,
      selectedService,
      theme: isDarkMode ? 'dark' : 'light'
    });

    showAlert(generalTitle, settingsSaved);
  };

  return (
    <>
      <MessageDialog
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        okText={t('buttons.ok')}
      />

      <div style={{ padding: "20px", maxWidth: "600px", margin: "0 auto" }}>
        {/* Appearance Settings */}
        <SettingsSection title={appearanceTitle}>
          <SettingRow label={themeLabel} description={themeDescription}>
            <Button
              appearance="subtle"
              icon={isDarkMode ? <WeatherSunny24Regular /> : <WeatherMoon24Regular />}
              onClick={onToggleTheme}
              aria-label={themeSwitchAriaLabel}
            >
              {currentThemeLabel}
            </Button>
          </SettingRow>

          <SettingRow label={languageLabel} description={languageDescription}>
            <LanguageSelector
              selectedLanguage={selectedLanguage}
              onLanguageChange={handleLanguageChange}
            />
          </SettingRow>
        </SettingsSection>

        {/* General Settings */}
        <SettingsSection title={generalTitle}>
          <SettingRow label={autoSaveLabel} description={autoSaveDescription}>
            <Switch 
              checked={settings.autoSave}
              onChange={(_, data) => handleAutoSaveChange(data.checked)}
            />
          </SettingRow>
        </SettingsSection>

        {/* API Configuration */}
        <SettingsSection title={apiConfigTitle}>
          <ServiceSelector
            label={serviceLabel}
            description={serviceDescription}
            openAILabel={openAILabel}
            azureLabel={azureLabel}
            openAICompatibleLabel={openAICompatibleLabel}
            anthropicLabel={anthropicLabel}
            bedrockLabel={bedrockLabel}
            bedrockAgentCoreLabel={bedrockAgentCoreLabel}
            selectedService={selectedService}
            onServiceChange={handleServiceChange}
          />

          <ApiConfiguration
            apiKeyLabel={apiKeyLabel}
            apiKeyPlaceholder={apiKeyPlaceholder}
            apiKeyDescription={apiKeyDescription}
            apiKey={localApiKey}
            onApiKeyChange={handleApiKeyChange}
            
            apiUrlLabel={apiUrlLabel}
            apiUrlPlaceholder={apiUrlPlaceholder}
            apiUrlDescription={apiUrlDescription}
            apiUrl={localApiUrl}
            onApiUrlChange={handleApiUrlChange}
            
            selectedService={selectedService}
          />

          <Button 
            appearance="primary" 
            icon={<Save24Regular />}
            onClick={handleSaveSettings}
            style={{ marginTop: '12px' }}
          >
            {saveSettingsLabel}
          </Button>
        </SettingsSection>

        <Divider />

        {/* About Section */}
        <SettingsSection title={aboutTitle}>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '2px',
            marginBottom: '12px'
          }}>
            <Caption1 style={{ 
              color: tokens.colorNeutralForeground3,
              fontSize: '12px',
              lineHeight: '16px'
            }}>
              {t('settings.distributedBy')}
            </Caption1>
            <Caption1 style={{ 
              color: tokens.colorNeutralForeground3,
              fontSize: '12px',
              lineHeight: '16px'
            }}>
              {t('settings.publisher')}: Articent Group
            </Caption1>
            <Caption1 style={{ 
              color: tokens.colorNeutralForeground3,
              fontSize: '12px',
              lineHeight: '16px'
            }}>
              {t('settings.version')}: {process.env.VERSION}
            </Caption1>
          </div>
          
          {/* Description */}
          <div style={{ marginTop: '12px' }}>
            <Text>
              {t('description', 'AI-powered assistant for Excel that helps with data analysis, formula generation, and automation tasks.')}
            </Text>
          </div>

          <div className={styles.linksList}>
            {/* Send feedback (email) */}
            <Link
              className={styles.linksItem}
              onClick={handleSendFeedback}
            >
              <Mail24Regular className={styles.linksIcon} />
              <Text>{t('settings.sendFeedback')}</Text>
            </Link>

            {/* More apps */}
            <Link
              className={styles.linksItem}
              href={MORE_APPS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Apps24Regular className={styles.linksIcon} />
              <Text>{t('settings.moreApps')}</Text>
            </Link>

            {/* Privacy policy */}
            <Link
              className={styles.linksItem}
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ShieldCheckmark24Regular className={styles.linksIcon} />
              <Text>{t('settings.privacyPolicy')}</Text>
            </Link>

            {/* Rate us */}
            <Link
              className={styles.linksItem}
              href={RATE_US_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Star24Regular className={styles.linksIcon} />
              <Text>{t('settings.rateUs')}</Text>
            </Link>
          </div>
        </SettingsSection>
      </div>
    </>
  );
};

export default SettingsPage;